import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { professionalProfiles, serviceCategories } from '@afficheservices/db/schema'
import { eq, and, sql, desc, isNull } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import { formatRating } from '~/lib/format'
import { MapPin, Star, BadgeCheck, ArrowRight, Search } from 'lucide-react'

// ─── Region data ─────────────────────────────────────────────

const REGIONS: Record<string, { name: string; departments: string[]; cities: string[] }> = {
  'ile-de-france': { name: 'Île-de-France', departments: ['75', '77', '78', '91', '92', '93', '94', '95'], cities: ['Paris', 'Boulogne-Billancourt', 'Saint-Denis', 'Montreuil', 'Argenteuil', 'Versailles', 'Nanterre', 'Créteil', 'Évry', 'Meaux'] },
  'auvergne-rhone-alpes': { name: 'Auvergne-Rhône-Alpes', departments: ['01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '73', '74'], cities: ['Lyon', 'Grenoble', 'Saint-Étienne', 'Clermont-Ferrand', 'Annecy', 'Valence', 'Chambéry', 'Villeurbanne', 'Vénissieux', 'Bourg-en-Bresse'] },
  'provence-alpes-cote-d-azur': { name: 'Provence-Alpes-Côte d\'Azur', departments: ['04', '05', '06', '13', '83', '84'], cities: ['Marseille', 'Nice', 'Toulon', 'Aix-en-Provence', 'Avignon', 'Cannes', 'Antibes', 'Fréjus', 'Arles', 'Gap'] },
  'nouvelle-aquitaine': { name: 'Nouvelle-Aquitaine', departments: ['16', '17', '19', '23', '24', '33', '40', '47', '64', '79', '86', '87'], cities: ['Bordeaux', 'Limoges', 'Poitiers', 'Pau', 'La Rochelle', 'Angoulême', 'Bayonne', 'Agen', 'Périgueux', 'Niort'] },
  'occitanie': { name: 'Occitanie', departments: ['09', '11', '12', '30', '31', '32', '34', '46', '48', '65', '66', '81', '82'], cities: ['Toulouse', 'Montpellier', 'Nîmes', 'Perpignan', 'Béziers', 'Narbonne', 'Albi', 'Tarbes', 'Carcassonne', 'Rodez'] },
  'hauts-de-france': { name: 'Hauts-de-France', departments: ['02', '59', '60', '62', '80'], cities: ['Lille', 'Amiens', 'Roubaix', 'Tourcoing', 'Dunkerque', 'Calais', 'Valenciennes', 'Boulogne-sur-Mer', 'Arras', 'Beauvais'] },
  'grand-est': { name: 'Grand Est', departments: ['08', '10', '51', '52', '54', '55', '57', '67', '68', '88'], cities: ['Strasbourg', 'Reims', 'Metz', 'Mulhouse', 'Nancy', 'Colmar', 'Troyes', 'Charleville-Mézières', 'Épinal', 'Thionville'] },
  'pays-de-la-loire': { name: 'Pays de la Loire', departments: ['44', '49', '53', '72', '85'], cities: ['Nantes', 'Angers', 'Le Mans', 'Saint-Nazaire', 'La Roche-sur-Yon', 'Cholet', 'Laval', 'Saumur'] },
  'bretagne': { name: 'Bretagne', departments: ['22', '29', '35', '56'], cities: ['Rennes', 'Brest', 'Quimper', 'Lorient', 'Vannes', 'Saint-Brieuc', 'Saint-Malo', 'Lannion'] },
  'normandie': { name: 'Normandie', departments: ['14', '27', '50', '61', '76'], cities: ['Rouen', 'Le Havre', 'Caen', 'Cherbourg', 'Évreux', 'Dieppe', 'Alençon', 'Lisieux'] },
  'bourgogne-franche-comte': { name: 'Bourgogne-Franche-Comté', departments: ['21', '25', '39', '58', '70', '71', '89', '90'], cities: ['Dijon', 'Besançon', 'Belfort', 'Auxerre', 'Chalon-sur-Saône', 'Mâcon', 'Nevers', 'Lons-le-Saunier'] },
  'centre-val-de-loire': { name: 'Centre-Val de Loire', departments: ['18', '28', '36', '37', '41', '45'], cities: ['Tours', 'Orléans', 'Bourges', 'Blois', 'Chartres', 'Châteauroux', 'Dreux', 'Vierzon'] },
  'corse': { name: 'Corse', departments: ['2A', '2B'], cities: ['Ajaccio', 'Bastia', 'Porto-Vecchio', 'Corte', 'Calvi'] },
}

// ─── Loader ──────────────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const regionSlug = params.region ?? ''
  const region = REGIONS[regionSlug]
  if (!region) throw new Response('Région non trouvée', { status: 404 })

  // Pros in this region
  const pros = await db
    .select({
      companyName: professionalProfiles.companyName,
      slug: professionalProfiles.slug,
      city: professionalProfiles.city,
      ratingAvg: professionalProfiles.ratingAvg,
      reviewCount: professionalProfiles.reviewCount,
      identityVerified: professionalProfiles.identityVerified,
    })
    .from(professionalProfiles)
    .where(and(
      sql`${professionalProfiles.departmentCode} IN (${sql.join(region.departments.map(d => sql`${d}`), sql`, `)})`,
      eq(professionalProfiles.identityVerified, true),
    ))
    .orderBy(desc(professionalProfiles.ratingAvg))
    .limit(20)

  // Categories available
  const categories = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  // Other regions for interlinking
  const otherRegions = Object.entries(REGIONS)
    .filter(([slug]) => slug !== regionSlug)
    .map(([slug, r]) => ({ slug, name: r.name }))

  return { regionSlug, region, pros, categories, otherRegions }
}

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: `Artisans en ${data?.region?.name ?? 'France'} — Professionnels vérifiés | AfficheServices` },
  { name: 'description', content: `Trouvez des artisans vérifiés en ${data?.region?.name}. Plombier, électricien, peintre et plus — devis gratuits sous 48h.` },
  { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/artisans/${data?.regionSlug}` },
]

function slugify(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

export default function ArtisansRegion() {
  const { regionSlug, region, pros, categories, otherRegions } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Artisans', url: 'https://www.afficheservices.fr/artisans' },
        { name: region.name, url: `https://www.afficheservices.fr/artisans/${regionSlug}` },
      ]} />

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <MapPin className="h-7 w-7 text-primary" />
          </div>
          <h1 className="mt-4 text-3xl font-bold text-slate-900">Artisans en {region.name}</h1>
          <p className="mt-2 text-sm text-slate-500">
            {pros.length} professionnels vérifiés · {categories.length} catégories de services
          </p>
        </div>

        {/* Services x Cities matrix */}
        <div className="mt-10">
          <h2 className="text-lg font-bold text-slate-900">Trouvez un artisan par ville et service</h2>
          <div className="mt-4 space-y-4">
            {region.cities.map((city) => (
              <div key={city} className="rounded-xl border border-border bg-white p-4 shadow-xs">
                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <MapPin className="h-3.5 w-3.5 text-primary" /> {city}
                </h3>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {categories.slice(0, 10).map((cat) => {
                    const svc = SERVICE_LABELS[cat.slug]
                    return (
                      <Link
                        key={cat.slug}
                        to={`/services/${cat.slug}/${slugify(city)}`}
                        className="rounded-full border border-slate-100 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary"
                      >
                        {svc?.artisan ?? cat.name} {city}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top pros */}
        {pros.length > 0 && (
          <div className="mt-10">
            <h2 className="text-lg font-bold text-slate-900">Meilleurs artisans en {region.name}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {pros.map((pro) => (
                <Link
                  key={pro.slug}
                  to={`/pro/${pro.slug}`}
                  className="card-hover flex items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-xs"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                    {pro.companyName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">{pro.companyName}</span>
                      {pro.identityVerified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {formatRating(pro.ratingAvg)} ({pro.reviewCount})
                      <span>·</span> {pro.city}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick links by service type */}
        <div className="mt-10">
          <h2 className="text-lg font-bold text-slate-900">Services en {region.name}</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat) => {
              const svc = SERVICE_LABELS[cat.slug]
              return (
                <Link
                  key={cat.slug}
                  to={`/services/${cat.slug}`}
                  className="flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary"
                >
                  <Search className="h-4 w-4 text-slate-400" />
                  {svc?.artisan ? `${svc.artisan.charAt(0).toUpperCase()}${svc.artisan.slice(1)}` : cat.name} en {region.name}
                </Link>
              )
            })}
          </div>
        </div>

        {/* Other regions */}
        <div className="mt-10">
          <h2 className="text-lg font-bold text-slate-900">Autres régions</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {otherRegions.map((r) => (
              <Link
                key={r.slug}
                to={`/artisans/${r.slug}`}
                className="rounded-full border border-border bg-white px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary"
              >
                {r.name}
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-2xl bg-slate-900 p-8 text-center">
          <h2 className="text-xl font-bold text-white">Trouvez le meilleur artisan en {region.name}</h2>
          <p className="mt-2 text-sm text-slate-400">3 devis gratuits d'artisans vérifiés sous 48h</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer contextTitle="Artisans par région" contextLinks={
        otherRegions.slice(0, 6).map((r) => ({ href: `/artisans/${r.slug}`, label: `Artisans ${r.name}` }))
      } />
    </div>
  )
}
